/**
 * Non-intrusive update notification badge for the header bar.
 * Shows an amber dot when an update is available, with a dropdown
 * containing release info, a "View release" link, and a "Dismiss" button.
 */

import { eventBus } from '@/core/events';
import { dismissUpdate } from '@/app/updater';
/** Create the update badge container and wire it to the event bus. */
export function createUpdateBadge(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'update-badge-container';
  container.style.cssText = 'position: relative; display: none;';

  // Amber dot indicator
  const dot = document.createElement('button');
  dot.className = 'header-btn update-badge-btn';
  dot.title = 'Update available';
  dot.setAttribute('aria-label', 'Update available');
  dot.style.cssText = `
    position: relative; background: none; border: none; cursor: pointer;
    font-size: 14px; padding: 4px 8px; color: var(--text-primary, #e0e0e0);
  `;
  dot.textContent = '\u2191'; // up arrow — update indicator
  container.appendChild(dot);

  // Amber indicator dot overlaid on the gear
  const indicator = document.createElement('span');
  indicator.style.cssText = `
    position: absolute; top: 2px; right: 4px;
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--accent-amber); border: 2px solid var(--bg-primary);
    pointer-events: none;
  `;
  dot.appendChild(indicator);

  // Dropdown
  const dropdown = document.createElement('div');
  dropdown.className = 'update-badge-dropdown';
  dropdown.style.cssText = `
    display: none; position: absolute; top: 100%; right: 0;
    background: var(--bg-secondary, #161616); border: 1px solid var(--border-primary, #2a2a2a);
    border-radius: 6px; padding: 12px; min-width: 220px; z-index: 9999;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4); font-size: 12px;
    color: var(--text-primary, #e0e0e0);
  `;
  container.appendChild(dropdown);

  // Track state
  let latestVersion = '';
  let releaseUrl = '';
  let dropdownVisible = false;

  // Toggle dropdown on dot click
  dot.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdownVisible = !dropdownVisible;
    dropdown.style.display = dropdownVisible ? 'block' : 'none';
  });

  // Close dropdown on outside click
  const closeHandler = () => {
    if (dropdownVisible) {
      dropdownVisible = false;
      dropdown.style.display = 'none';
    }
  };
  document.addEventListener('click', closeHandler);

  // Prevent dropdown clicks from closing it
  dropdown.addEventListener('click', (e) => {
    e.stopPropagation();
  });

  function renderDropdown(): void {
    dropdown.textContent = '';

    // Version label
    const label = document.createElement('div');
    label.style.cssText = 'margin-bottom: 8px; font-weight: 600; color: var(--accent-amber);';
    label.textContent = `Update available: v${latestVersion.replace(/^v/, '')}`;
    dropdown.appendChild(label);

    // View release link
    const link = document.createElement('a');
    link.href = releaseUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.cssText = `
      display: block; margin-bottom: 8px; color: var(--accent);
      text-decoration: none; cursor: pointer;
    `;
    link.textContent = 'View release';
    link.addEventListener('mouseenter', () => { link.style.textDecoration = 'underline'; });
    link.addEventListener('mouseleave', () => { link.style.textDecoration = 'none'; });
    dropdown.appendChild(link);

    // Dismiss button
    const dismissBtn = document.createElement('button');
    dismissBtn.style.cssText = `
      background: var(--bg-tertiary, #1c1c1c); border: 1px solid var(--border-primary, #2a2a2a);
      color: var(--text-secondary, #999999); padding: 4px 12px; border-radius: 4px;
      cursor: pointer; font-size: 11px; width: 100%;
    `;
    dismissBtn.textContent = 'Dismiss';
    dismissBtn.addEventListener('click', () => {
      dismissUpdate(latestVersion);
      container.style.display = 'none';
      dropdownVisible = false;
      dropdown.style.display = 'none';
    });
    dropdown.appendChild(dismissBtn);
  }

  // Listen for update events
  const unsubUpdate = eventBus.on('update:available', (data) => {
    latestVersion = data.latestVersion;
    releaseUrl = data.releaseUrl;
    container.style.display = 'block';
    renderDropdown();
  });

  // Attach destroy for teardown
  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    unsubUpdate();
    document.removeEventListener('click', closeHandler);
  };

  return container;
}
